import axios, { AxiosError } from "axios";
import { Identity } from "../models/LoginInfo";
import { NotifiableObservable, Observable } from "./Observable";

const LoginInfoStorageKey = "loginInfo";
class UserService {
  private _loginInfo?: Identity;
  private observable = new NotifiableObservable<Identity | undefined>();

  get authChangeObservable(): Observable<Identity | undefined> {
    return this.observable;
  }

  constructor() {
    const json = sessionStorage.getItem(LoginInfoStorageKey) || localStorage.getItem(LoginInfoStorageKey);
    if (json) {
      this._loginInfo = JSON.parse(json) as Identity;
      this.observable.notify(this._loginInfo);
    }
  }

  public getUserInfo = () => this._loginInfo;

  public register = async (
    username: string,
    password: string,
    name: string,
    teamName: string
  ): Promise<{ isSuccess: boolean; error: string | undefined }> => {
    const response = await axios.post<{ error?: string }>(`${process.env.REACT_APP_API_URL}/account/register`, {
      username: username,
      password: password,
      name: name,
      teamName: teamName,
    });
    if (response.status !== 200) {
      return { isSuccess: false, error: response.data.error || "An unknown error occured" };
    }
    return { isSuccess: true, error: undefined };
  };

  public login = async (username: string, password: string, rememberMe: boolean): Promise<boolean> => {
    try {
      const response = await axios.post<Identity>(`${process.env.REACT_APP_API_URL}/account/login`, { username: username, password: password });
      if (response.status !== 200) {
        return false;
      }
      this._loginInfo = response.data;
      const storage = rememberMe ? localStorage : sessionStorage;
      storage.setItem(LoginInfoStorageKey, JSON.stringify(this._loginInfo));
      this.observable.notify(this._loginInfo);
      return true;
    } catch (e: any | AxiosError) {
      console.error(e);
      return false;
    }
  };

  public logout = () => {
    localStorage.removeItem(LoginInfoStorageKey);
    sessionStorage.removeItem(LoginInfoStorageKey);
    this._loginInfo = undefined;
  };

  public updateTeamName = async (teamName: string): Promise<{ errors: string[] } | undefined> => {
    try {
      const response = await axios.put<{ errors: string[] } | undefined>(`${process.env.REACT_APP_API_URL}/account/teamName`, { teamName: teamName });
      return response.data;
    } catch (err: any | AxiosError) {
      if (axios.isAxiosError(err)) {
        return err.response?.data;
      } else {
        return { errors: ["An unknown error occured. Please try again."] };
      }
    }
  };

  public changePassword = async (currentPassword: string, password: string) => {
    const response = await axios.put<{ errors: string[] } | undefined>(`${process.env.REACT_APP_API_URL}/account/password`, {
      password: password,
      currentPassword: currentPassword,
    });
    return response.data;
  };

  public requestPasswordReset = async (username: string): Promise<void> => {
    await axios.post(`${process.env.REACT_APP_API_URL}/account/forgotPassword`, { username });
  };

  public resetPassword = async (username: string, token: string, password: string): Promise<{ errors?: string[] }> => {
    try {
      await axios.post(`${process.env.REACT_APP_API_URL}/account/resetPassword`, { username, token, password });
      return {};
    } catch (err: any | AxiosError) {
      if (axios.isAxiosError(err) && err.response?.data?.errors) {
        return err.response.data;
      }
      return { errors: ["An unknown error occurred. Please try again."] };
    }
  };
}

export default new UserService();
